import React from 'react';
import moment from 'moment';
import i18n from 'i18n';
import classNames from 'classnames/bind';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faExclamationTriangle } from '@fortawesome/free-solid-svg-icons';
import { ColumnDescription } from 'components/common/Table/types';
import {
  getDefaultColumnDescription,
  renderColumn
} from 'components/TableColumn/TableColumn';
import { WeatherType } from 'core/limitation/Limitation';
import {
  dedupeConditionLabel,
  DIMENSION_TO_WEATHER_TYPE,
  UNMATCHED_DIMENSION_I18N,
  UV_INDEX_LEVEL_I18N,
  WEATHER_TYPE_I18N,
  WeatherVerificationCondition,
  WeatherVerificationDeviceStatus,
  WeatherVerificationReasonType,
  WeatherVerificationResult,
  WeatherVerificationUnmatchedReason
} from 'core/report/WeatherVerification';
import {
  isIgnoredSpaceHealthState,
  parseAggregatedHealthState,
  SpaceHealthState,
  SPACE_HEALTH_STATUS_I18N
} from 'enum/SpaceHealthState';
import { WeatherVerificationTableRecord } from './WeatherVerificationTabModel';
import styles from './weatherVerificationTab.module.scss';

const cx = classNames.bind(styles);

const EMPTY_VALUE = '—';

const RESULT_STYLE: Record<WeatherVerificationResult, string> = {
  [WeatherVerificationResult.DELIVERED]: 'delivered',
  [WeatherVerificationResult.WEATHER_UNMATCHED]: 'weatherUnmatched',
  [WeatherVerificationResult.DEVICE_ABNORMAL]: 'deviceAbnormal'
};

const RESULT_I18N: Record<WeatherVerificationResult, string> = {
  [WeatherVerificationResult.DELIVERED]:
    'weatherVerification.results.delivered',
  [WeatherVerificationResult.WEATHER_UNMATCHED]:
    'weatherVerification.results.weatherUnmatched',
  [WeatherVerificationResult.DEVICE_ABNORMAL]:
    'weatherVerification.results.deviceAbnormal'
};

/**
 * 各維度實況值的顯示格式。欄位與未投遞原因的 tooltip 共用同一份，
 * 否則會出現「欄位顯示『下雨』、tooltip 卻顯示原始代碼 21」這種不一致。
 */
const CONDITION_VALUE_FORMATTERS: Record<
  WeatherType,
  (condition: WeatherVerificationCondition) => string
> = {
  [WeatherType.WEATHER_CODE]: (condition) =>
    condition.weatherType && WEATHER_TYPE_I18N[condition.weatherType]
      ? i18n.t(WEATHER_TYPE_I18N[condition.weatherType])
      : `${condition.value}`,
  [WeatherType.TEMPERATURE]: (condition) => `${condition.value}°C`,
  [WeatherType.RELATIVE_HUMIDITY]: (condition) => `${condition.value}%`,
  [WeatherType.UV_INDEX]: (condition) =>
    condition.uvIndexLevel && UV_INDEX_LEVEL_I18N[condition.uvIndexLevel]
      ? i18n.t(UV_INDEX_LEVEL_I18N[condition.uvIndexLevel])
      : `${condition.value}`,
  [WeatherType.PROBABILITY_OF_PRECIPITATION]: (condition) =>
    `${condition.value}%`
};

export const formatConditionValue = (
  weatherType: WeatherType,
  condition?: WeatherVerificationCondition
): string =>
  condition && condition.value !== undefined
    ? CONDITION_VALUE_FORMATTERS[weatherType](condition)
    : EMPTY_VALUE;

/**
 * 天氣不符「而且」機台同時異常。
 *
 * 後端的 `result` 是三態，天氣一不符就歸 `WEATHER_UNMATCHED`（不看機台），
 * 但 `deviceStatus.abnormal` 是獨立計算的，所以這個組合要由前端自己判。
 */
export const isWeatherAndDeviceAbnormal = (
  record: Pick<WeatherVerificationTableRecord, 'result' | 'deviceStatus'>
): boolean =>
  record.result === WeatherVerificationResult.WEATHER_UNMATCHED &&
  record.deviceStatus?.abnormal === true;

/** 結果的文字，畫面 badge 與 CSV 共用。 */
export const getResultLabel = (
  record: Pick<WeatherVerificationTableRecord, 'result' | 'deviceStatus'>
): string =>
  isWeatherAndDeviceAbnormal(record)
    ? i18n.t('weatherVerification.results.weatherAndDeviceAbnormal')
    : i18n.t(RESULT_I18N[record.result]);

/** 未投遞原因的文字清單，畫面 tag 與 CSV 共用。 */
export const getUnmatchedReasonLabels = (
  reasons: WeatherVerificationUnmatchedReason[]
): string[] =>
  (reasons ?? []).map((reason) =>
    reason.type === WeatherVerificationReasonType.DEVICE
      ? i18n.t('weatherVerification.reasons.device')
      : reason.dimension
        ? i18n.t(UNMATCHED_DIMENSION_I18N[reason.dimension])
        : ''
  );

const renderEmpty = () => (
  <span className={styles.emptyValue}>{EMPTY_VALUE}</span>
);

/**
 * 機台狀態文字。API 文件標記中文對照未定案，這裡沿用機台管理頁既有的
 * `spaceHealth.labels.*`（停止服務／服務啟動中／訊號異常／正常），
 * 對不到才退回後端給的原始 `spaceStatus`。
 */
export const getDeviceStatusText = (
  deviceStatus?: WeatherVerificationDeviceStatus
): string => {
  if (!deviceStatus || deviceStatus.noStatusData) {
    return i18n.t('weatherVerification.labels.noStatusData');
  }
  const healthState = parseAggregatedHealthState(deviceStatus.healthStates);
  if (healthState) {
    return i18n.t(SPACE_HEALTH_STATUS_I18N[healthState]);
  }
  // ECOPD-5564：SSP 新增、PMax 一律當成正常的狀態會被 parse 成 undefined，不能讓它
  // 掉到下面的 fallback 顯示成後端原始英文字串（例「ON」）。整組都是這些值時才算
  // 正常；混到真正未知的值仍走 fallback，維持既有行為。
  // 包成陣列的方式與 parseAggregatedHealthState 一致 —— 型別雖標成 string[]，
  // 但 wire 層無驗證，單一字串進來時兩邊要有同樣的解讀，也才不會 throw。
  const states = Array.isArray(deviceStatus.healthStates)
    ? deviceStatus.healthStates
    : [deviceStatus.healthStates];
  if (states.length > 0 && states.every(isIgnoredSpaceHealthState)) {
    return i18n.t(SPACE_HEALTH_STATUS_I18N[SpaceHealthState.NORMAL]);
  }
  return deviceStatus.spaceStatus ?? EMPTY_VALUE;
};

const renderDeviceAbnormalTooltip = (
  id: string,
  deviceStatus: WeatherVerificationDeviceStatus | undefined,
  children: JSX.Element,
  /** 在 `.map()` 內使用時必填 —— React 只認得 map 實際回傳的那個元素上的 key */
  key?: string,
  /** 開頭那句說明；天氣同時不符時說法不一樣 */
  leadingI18nKey = 'weatherVerification.descriptions.deviceAbnormalTooltip'
) => (
  <OverlayTrigger
    key={key}
    placement='top'
    overlay={
      <Tooltip id={id}>
        <div>{i18n.t(leadingI18nKey)}</div>
        <div>
          {i18n.t('weatherVerification.labels.deviceStatus', {
            status: getDeviceStatusText(deviceStatus)
          })}
        </div>
        {deviceStatus?.spaceStatusModifyReason && (
          <div>
            {i18n.t('weatherVerification.labels.deviceStatusReason', {
              reason: deviceStatus.spaceStatusModifyReason
            })}
          </div>
        )}
        <div>{i18n.t('weatherVerification.descriptions.checkDevicePage')}</div>
      </Tooltip>
    }
  >
    {children}
  </OverlayTrigger>
);

const resultFormatter = (
  result: WeatherVerificationResult,
  row: WeatherVerificationTableRecord
) => {
  // 天氣不符且機台同時異常時，沿用機台異常的橘色與警告圖示，但文字兩者都講
  const bothAbnormal = isWeatherAndDeviceAbnormal(row);
  const isDeviceAbnormal = result === WeatherVerificationResult.DEVICE_ABNORMAL;
  const showsDeviceIssue = bothAbnormal || isDeviceAbnormal;
  const badge = (
    <span
      className={cx(
        'resultBadge',
        bothAbnormal
          ? RESULT_STYLE[WeatherVerificationResult.DEVICE_ABNORMAL]
          : RESULT_STYLE[result]
      )}
    >
      {showsDeviceIssue ? (
        <FontAwesomeIcon icon={faExclamationTriangle} />
      ) : (
        <span className={cx('dot', 'matched')} />
      )}
      {getResultLabel(row)}
    </span>
  );
  if (!showsDeviceIssue) {
    return badge;
  }
  return renderDeviceAbnormalTooltip(
    `resultTooltip_${row.key}`,
    row.deviceStatus,
    badge,
    undefined,
    bothAbnormal
      ? 'weatherVerification.descriptions.weatherAndDeviceTooltip'
      : 'weatherVerification.descriptions.deviceAbnormalTooltip'
  );
};

/** 條件欄位的實際值。活動未綁定該維度時 `matched` 不存在，就不畫符合指示點。 */
const conditionFormatter =
  (weatherType: WeatherType) =>
  // 這是 table 的 cell formatter，不是 React component
  // eslint-disable-next-line react/display-name
  (_cell, row: WeatherVerificationTableRecord) => {
    const condition = row.conditions?.[weatherType];
    if (!condition) {
      return renderEmpty();
    }
    const text = formatConditionValue(weatherType, condition);
    return (
      <span className={styles.conditionValue}>
        {condition.matched !== undefined && (
          <span
            className={cx('dot', condition.matched ? 'matched' : 'unmatched')}
          />
        )}
        {text}
      </span>
    );
  };

const unmatchedReasonsFormatter = (
  reasons: WeatherVerificationUnmatchedReason[],
  row: WeatherVerificationTableRecord
) => {
  if (!reasons || reasons.length === 0) {
    return renderEmpty();
  }
  // 天氣同時不符時，機台 tag 的 hover 說法要跟著換 ——
  // 否則會出現「天氣條件符合，但機台未投遞」這種與該列矛盾的文字
  const deviceTooltipKey = isWeatherAndDeviceAbnormal(row)
    ? 'weatherVerification.descriptions.weatherAndDeviceTooltip'
    : 'weatherVerification.descriptions.deviceAbnormalTooltip';
  return (
    <span className={styles.reasonTags}>
      {reasons.map((reason, index) => {
        if (reason.type === WeatherVerificationReasonType.DEVICE) {
          return renderDeviceAbnormalTooltip(
            `reasonTooltip_${row.key}_${index}`,
            row.deviceStatus,
            <span className={cx('reasonTag', 'device')}>
              {i18n.t('weatherVerification.reasons.device')}
            </span>,
            `device_${index}`,
            deviceTooltipKey
          );
        }
        const label = reason.dimension
          ? i18n.t(UNMATCHED_DIMENSION_I18N[reason.dimension])
          : '';
        // 「實際」要跟左側條件欄位顯示同一個值（例如天氣現象顯示「下雨」而不是原始代碼 21），
        // 所以優先用該列的 condition 走共用 formatter，取不到才退回原始 actual。
        const weatherType = reason.dimension
          ? DIMENSION_TO_WEATHER_TYPE[reason.dimension]
          : undefined;
        const condition = weatherType
          ? row.conditions?.[weatherType]
          : undefined;
        const actualText =
          weatherType && condition
            ? formatConditionValue(weatherType, condition)
            : (reason.actual ?? EMPTY_VALUE);
        // 設定與實際各佔一行，長條件才不會擠成一團
        const detail = reason.target ? (
          <>
            <div>
              {i18n.t('weatherVerification.descriptions.unmatchedTarget', {
                target: dedupeConditionLabel(reason.target.label)
              })}
            </div>
            <div>
              {i18n.t('weatherVerification.descriptions.unmatchedActual', {
                actual: actualText
              })}
            </div>
          </>
        ) : undefined;
        const tag = (
          <span
            className={cx('reasonTag', 'weather')}
            key={`${reason.dimension}_${index}`}
          >
            {label}
          </span>
        );
        if (!detail) {
          return tag;
        }
        return (
          <OverlayTrigger
            key={`${reason.dimension}_${index}`}
            placement='top'
            overlay={
              <Tooltip id={`weatherReason_${row.key}_${index}`}>
                {detail}
              </Tooltip>
            }
          >
            {tag}
          </OverlayTrigger>
        );
      })}
    </span>
  );
};

export const getWeatherVerificationColumns = (): ColumnDescription[] => [
  renderColumn(
    getDefaultColumnDescription(
      'dataTime',
      'weatherVerification.headers.dataTime',
      false
    ),
    (dataTime: string) => moment(dataTime).format('MM/DD HH:00')
  ),
  renderColumn(
    getDefaultColumnDescription(
      'storeName',
      'weatherVerification.headers.storeName',
      false
    )
  ),
  renderColumn(
    getDefaultColumnDescription(
      'result',
      'weatherVerification.headers.result',
      false
    ),
    resultFormatter
  ),
  renderColumn(
    getDefaultColumnDescription(
      WeatherType.WEATHER_CODE,
      `limitation.labels.${WeatherType.WEATHER_CODE}`,
      false
    ),
    conditionFormatter(WeatherType.WEATHER_CODE)
  ),
  renderColumn(
    getDefaultColumnDescription(
      WeatherType.TEMPERATURE,
      `limitation.labels.${WeatherType.TEMPERATURE}`,
      false
    ),
    conditionFormatter(WeatherType.TEMPERATURE)
  ),
  renderColumn(
    getDefaultColumnDescription(
      WeatherType.RELATIVE_HUMIDITY,
      `limitation.labels.${WeatherType.RELATIVE_HUMIDITY}`,
      false
    ),
    conditionFormatter(WeatherType.RELATIVE_HUMIDITY)
  ),
  renderColumn(
    getDefaultColumnDescription(
      WeatherType.UV_INDEX,
      `limitation.labels.${WeatherType.UV_INDEX}`,
      false
    ),
    conditionFormatter(WeatherType.UV_INDEX)
  ),
  renderColumn(
    getDefaultColumnDescription(
      'unmatchedReasons',
      'weatherVerification.headers.unmatchedReasons',
      false
    ),
    unmatchedReasonsFormatter
  )
];
